import { useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion } from 'motion/react'
import ReactCountryFlag from 'react-country-flag'
import { Alert, Button, DataBlock, Spinner } from '@/common'
import type { ValidatedImportedGoodRow } from '@/features/cbam-reporting/hooks/importedGoodsGRR'
import useI18n from '@/shared/hooks/usei18n'
import CnCodeTag from '@/shared/components/CnCodeTag'
import { ListHeader, ListHeaderCell, ListRow } from './list/ImportedGoodsList'
import { ListRowCell } from './list/ImportedGoodListRow'
import { NetMassDisplayUnitToggle } from '@/shared/components/NetMassDisplayUnitToggle'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

type ItemWithEmissions = {
  item: ValidatedImportedGoodRow
  directEmissions: number | null
  indirectEmissions: number | null
}

/** Same estimate as ImportedGoodsVirtualRows (ListRow py-3 + grid content). */
const TABLE_ROW_ESTIMATE_PX = 88

function List({
  data,
  isLoadingLiability,
  isRepresentative,
  emissionsUnavailableLabel
}: {
  data: ItemWithEmissions[]
  isLoadingLiability: boolean
  isRepresentative: boolean
  emissionsUnavailableLabel: string
}) {
  const { t, numberFormatter3Decimals, shortDateFormatter } = useI18n()
  const { formatNetMass, netMassColumnLabel } = useNetMassDisplay()
  const scrollRef = useRef<HTMLDivElement>(null)
  const gridTemplateColumns = '1.75fr 1fr 1fr 1fr 0.5fr 0.75fr 0.75fr 0.75fr 0.5fr'

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => TABLE_ROW_ESTIMATE_PX,
    overscan: 8,
    gap: 0
  })

  return (
    <div className="border border-border-default rounded-xs overflow-x-auto w-full">
      <ListHeader gridTemplateColumns={gridTemplateColumns}>
        <ListHeaderCell className="w-20">
          {t('cbamReporting:importedGoodsUpload.table.headers.mrnNo')}
        </ListHeaderCell>
        <ListHeaderCell>
          {t('cbamReporting:importedGoodsUpload.table.headers.cnCode')}
        </ListHeaderCell>
        <ListHeaderCell>
          {t('cbamReporting:importedGoodsUpload.table.headers.importDate')}
        </ListHeaderCell>
        <ListHeaderCell>
          {t('cbamReporting:importedGoodsUpload.table.headers.sellerName')}
        </ListHeaderCell>
        {isRepresentative && (
          <ListHeaderCell>
            {t('cbamReporting:importedGoodsUpload.table.headers.importer')}
          </ListHeaderCell>
        )}
        <ListHeaderCell>
          {t('cbamReporting:importedGoodsUpload.table.headers.customsProcedure')}
        </ListHeaderCell>
        <ListHeaderCell className="text-right">{netMassColumnLabel}</ListHeaderCell>
        <ListHeaderCell className="text-right">
          {t('cbamReporting:importedGoodsUpload.table.headers.directEmissions')}
        </ListHeaderCell>
        <ListHeaderCell className="text-right">
          {t('cbamReporting:importedGoodsUpload.table.headers.indirectEmissions')}
        </ListHeaderCell>
      </ListHeader>
      <div
        ref={scrollRef}
        className="w-full max-h-[min(60vh,32rem)] overflow-y-auto overflow-x-hidden xl:bg-background-surface"
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative'
          }}
        >
          {virtualizer.getVirtualItems().map(virtualRow => {
            const row = data[virtualRow.index]
            return (
              <div
                key={`${row.item.customId}-${virtualRow.index}`}
                data-index={virtualRow.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`
                }}
              >
                <ListRow gridTemplateColumns={gridTemplateColumns}>
                  <ListRowCell className="truncate font-mono" title={row.item.customId}>
                    {row.item.customId ? row.item.customId : '-'}
                  </ListRowCell>
                  <ListRowCell className="truncate space-y-0.5">
                    <CnCodeTag cnCode={row.item.cnCode} />
                    {row.item.description && (
                      <span
                        title={row.item.description}
                        className="text-sm text-text-secondary text-ellipsis"
                      >
                        {row.item.description}
                      </span>
                    )}
                  </ListRowCell>
                  <ListRowCell>{shortDateFormatter(new Date(row.item.importDate))}</ListRowCell>
                  <ListRowCell className="">
                    <div className="flex items-center gap-1">
                      <ReactCountryFlag
                        className="align-middle -translate-y-0.5 mr-0.5"
                        countryCode={row.item.country}
                        svg
                      />

                      <span>{t(`common:countriesByIsoAlpha2.${row.item.country}`)}</span>
                    </div>
                    {row.item.sellerName && (
                      <span title={row.item.sellerName} className="text-sm text-text-secondary">
                        {row.item.sellerName}
                      </span>
                    )}
                  </ListRowCell>
                  {isRepresentative && (
                    <ListRowCell className="truncate">
                      {<span>{row.item.importerName ? row.item.importerName : '-'}</span>}
                    </ListRowCell>
                  )}
                  <ListRowCell>
                    {row.item.requestedCustomsProcedure ? row.item.requestedCustomsProcedure : '-'}
                  </ListRowCell>
                  <ListRowCell className="text-right">
                    {formatNetMass(row.item.netMassInTonnes)}
                  </ListRowCell>
                  <ListRowCell className="text-right">
                    {isLoadingLiability ? (
                      <Spinner className="size-4" />
                    ) : row.directEmissions === null ? (
                      <span className="text-text-secondary" title={emissionsUnavailableLabel}>
                        —
                      </span>
                    ) : (
                      numberFormatter3Decimals(row.directEmissions)
                    )}
                  </ListRowCell>
                  <ListRowCell className="text-right">
                    {isLoadingLiability ? (
                      <Spinner className="size-4" />
                    ) : row.indirectEmissions === null ? (
                      <span className="text-text-secondary" title={emissionsUnavailableLabel}>
                        —
                      </span>
                    ) : (
                      numberFormatter3Decimals(row.indirectEmissions)
                    )}
                  </ListRowCell>
                </ListRow>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function ImportedGoodsUploadSummary({
  data,
  itemsWithEmissions,
  totals,
  isLoadingLiability,
  isRepresentative,
  onBack,
  onCreateAll,
  onCancel,
  isCreating,
  isCreateDisabled = false,
  liabilityPreviewUnavailable = false,
  reportingPeriodYear: _reportingPeriodYear
}: {
  data: ValidatedImportedGoodRow[]
  itemsWithEmissions: ItemWithEmissions[]
  totals: {
    weight: number
    directEmissions: number | null
    indirectEmissions: number | null
  }
  isLoadingLiability: boolean
  /** When true, show notice that batch liability preview was not returned; emissions show as unavailable. */
  liabilityPreviewUnavailable?: boolean
  isRepresentative: boolean
  onBack: () => void
  onCreateAll: () => void
  onCancel: () => void
  isCreating: boolean
  /** When true, Create is disabled (e.g. GRR was not persisted — no id to finalize). */
  isCreateDisabled?: boolean
  reportingPeriodYear: number
}) {
  const { t, numberFormatter3Decimals } = useI18n()
  const { formatNetMass } = useNetMassDisplay()

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col gap-6 pb-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">
          {t('cbamReporting:importedGoodsUpload.summary.title')}
        </h2>
        <NetMassDisplayUnitToggle />
      </div>

      {liabilityPreviewUnavailable && (
        <Alert type="warning">
          <div className="space-y-1">
            <p className="font-medium">
              {t('cbamReporting:importedGoodsUpload.summary.liabilityPreviewUnavailable.title')}
            </p>
            <p className="text-sm">
              {t(
                'cbamReporting:importedGoodsUpload.summary.liabilityPreviewUnavailable.description'
              )}
            </p>
          </div>
        </Alert>
      )}

      {/* Stats summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <DataBlock
          label={t('cbamReporting:importedGoodsUpload.summary.totalGoods')}
          data={<span className="text-2xl font-semibold">{data.length}</span>}
        />
        <DataBlock
          label={t('cbamReporting:importedGoodsUpload.summary.totalWeight')}
          data={
            <span className="text-2xl font-semibold">{formatNetMass(totals.weight)}</span>
          }
          isLoading={isLoadingLiability}
        />
        <DataBlock
          label={t('cbamReporting:importedGoodsUpload.summary.totalDirectEmissions')}
          data={
            totals.directEmissions === null ? (
              <span
                className="text-2xl font-semibold text-text-secondary"
                title={t('cbamReporting:importedGoodsUpload.summary.emissionsUnavailable')}
              >
                —
              </span>
            ) : (
              <span className="text-2xl font-semibold">
                {numberFormatter3Decimals(totals.directEmissions)} tCO₂
              </span>
            )
          }
          isLoading={isLoadingLiability}
        />
        <DataBlock
          label={t('cbamReporting:importedGoodsUpload.summary.totalIndirectEmissions')}
          data={
            totals.indirectEmissions === null ? (
              <span
                className="text-2xl font-semibold text-text-secondary"
                title={t('cbamReporting:importedGoodsUpload.summary.emissionsUnavailable')}
              >
                —
              </span>
            ) : (
              <span className="text-2xl font-semibold">
                {numberFormatter3Decimals(totals.indirectEmissions)} tCO₂
              </span>
            )
          }
          isLoading={isLoadingLiability}
        />
      </div>

      {/* Progress indicator when creating */}
      {isCreating && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xs border border-blue-200">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
          <span className="text-blue-800">
            {t('cbamReporting:importedGoodsUpload.summary.creating')}
          </span>
        </div>
      )}

      <List
        data={itemsWithEmissions}
        isLoadingLiability={isLoadingLiability}
        isRepresentative={isRepresentative}
        emissionsUnavailableLabel={t(
          'cbamReporting:importedGoodsUpload.summary.emissionsUnavailable'
        )}
      />

      {/* Action */}
      <div className="sticky bottom-0 z-10 flex justify-between gap-4 pt-4 pb-1 -mx-1 px-1 border-t border-gray-300 bg-blue-200">
        <div className="flex flex-wrap gap-2">
          <Button variant="neutral" onClick={onCancel} disabled={isCreating}>
            {t('cbamReporting:importedGoodsUpload.cancelButton')}
          </Button>
          <Button variant="subtle" onClick={onBack} disabled={isCreating}>
            {t('cbamReporting:importedGoodsUpload.summary.backToValidation')}
          </Button>
        </div>
        <Button
          onClick={onCreateAll}
          isLoading={isCreating}
          disabled={isCreating || isCreateDisabled}
        >
          {t('cbamReporting:importedGoodsUpload.summary.createButton', { count: data.length })}
        </Button>
      </div>
    </motion.div>
  )
}
